// LoginForm.jsx
import React from 'react';
import { T } from "./Translate";
import {
  Alert,
  Box,
  Button,
  CardContent,
  CardMedia,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  Link,
  OutlinedInput,
  Stack,
  Typography,
  Container
} from '@mui/material';
import { Visibility, VisibilityOff } from '../icons';
import { brandColors } from "../theme";
import logo13 from "../logo13.png";
import { isIOSApp } from "../utils/device";
const logo15 = `${process.env.PUBLIC_URL}/logo15.png`;

// "G" de Google a color (las guías de marca piden el logo original).
const GoogleLogo = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
  </svg>
);

const LoginForm = ({
  formData,
  error,
  isLoading,
  showPassword,
  handleChange,
  handleSubmit,
  setShowPassword,
  handlePasskeyLogin,
  passkeyLoading,
  // passkeyAvailable: este dispositivo ya registró/usó una llave de acceso
  // (ver utils/passkeyDevice.js). En ese caso el login arranca en modo
  // passkey (passkeyMode), pero se puede pasar a email+contraseña con
  // onTogglePasskeyMode: en el mismo dispositivo puede haber otra cuenta
  // que no tiene llave y si no, no había forma de entrar con ella.
  passkeyMode,
  passkeyAvailable,
  onTogglePasskeyMode,
  handleGoogleLogin,
  googleLoading
}) => {
  return (
    <Container maxWidth="xs">
      <Box  sx={{ mt: 8, pb: 4 }}>
        <CardMedia component="img" image={brandColors.mode === 'dark' ? logo13 : logo15} title="Notiline" sx={{ width: 180, mx: 'auto', mt: 4, mb: 2 }} />
        <CardContent>
          <Typography variant="h5" textAlign="center" fontWeight="300" gutterBottom>
            <T>Inicio de sesión</T>
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            {!passkeyMode && (
              <>
                <FormControl fullWidth margin="normal">
                  <InputLabel htmlFor="email"><T>Email</T></InputLabel>
                  <OutlinedInput
                    id="email"
                    name="username"
                    type="email"
                    label={<T>Email</T>}
                    value={formData.email}
                    onChange={handleChange('email')}
                    placeholder="usuario@gmail.com"
                    disabled={isLoading}
                    autoComplete="username webauthn"
                  />
                </FormControl>

                <FormControl fullWidth margin="normal">
                  <InputLabel htmlFor="password"><T>Contraseña</T></InputLabel>
                  <OutlinedInput
                    id="password"
                    name="current-password"
                    type={showPassword ? 'text' : 'password'}
                    label={<T>Contraseña</T>}
                    value={formData.password}
                    onChange={handleChange('password')}
                    placeholder="••••••••••••"
                    disabled={isLoading}
                    autoComplete="current-password webauthn"
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" disabled={isLoading}>
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    }
                  />
                </FormControl>

                <Button type="submit" fullWidth variant="contained" disabled={isLoading} sx={{ mt: 3, height: 42 }}>
                  {isLoading ? <CircularProgress size={24} color="inherit" /> : <T>Iniciar sesión</T>}
                </Button>
              </>
            )}

            {passkeyMode && (
              <Button
                type="button"
                fullWidth
                variant="contained"
                onClick={handlePasskeyLogin}
                disabled={passkeyLoading}
                sx={{ mt: 3, height: 42 }}
              >
                {passkeyLoading ? <CircularProgress size={24} color="inherit" /> : <T>Iniciar sesión con llave de acceso</T>}
              </Button>
            )}

            {passkeyAvailable && (
              <Box sx={{ textAlign: 'center', mt: 1.5 }}>
                <Link component="button" type="button" variant="body2" onClick={onTogglePasskeyMode} sx={{ color: brandColors.accent }}>
                  {passkeyMode ? <T>Usar otra cuenta con email y contraseña</T> : <T>Usar llave de acceso</T>}
                </Link>
              </Box>
            )}

            {!isIOSApp() && (<>
            <Divider sx={{ my: 2, color: brandColors.textSecondary, fontSize: 13 }}>
              <T>o</T>
            </Divider>

            <Button
              type="button"
              fullWidth
              variant="outlined"
              onClick={handleGoogleLogin}
              disabled={googleLoading || isLoading}
              startIcon={!googleLoading && <GoogleLogo />}
              sx={{ height: 42, textTransform: 'none', fontWeight: 500 }}
            >
              {googleLoading ? (
                // Con texto y en color de marca: deshabilitado, el spinner solo
                // quedaba como un punto gris y parecía que la app se había colgado.
                <>
                  <CircularProgress size={20} sx={{ mr: 1.5, color: brandColors.accent }} />
                  <T>Ingresando con Google…</T>
                </>
              ) : <T>Continuar con Google</T>}
            </Button>
            </>)}

            <Stack spacing={1} alignItems="center" sx={{ mt: 2 }}>
              {!passkeyMode && (
                <Link href="/reset-password" variant="body2" disabled={isLoading} sx={{ color: brandColors.accent }}>
                  <T>Olvidé mi contraseña</T>
                </Link>
              )}
              <Link href="/account-recovery" variant="body2" sx={{ color: brandColors.textSecondary }}>
                <T>¿No podés acceder a tu cuenta?</T>
              </Link>
              <Typography variant="body2" sx={{ color: brandColors.textSecondary }}>
                <T>¿Aún no tienes cuenta?</T>{' '}
                <Link href="/register" sx={{ color: brandColors.accent }}><T>Registrarse</T></Link>
              </Typography>
            </Stack>
          </Box>
        </CardContent>
      </Box>
    </Container>
  );
};

export default LoginForm;
